import React, { useState, useRef, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { formatDueDate, isOverdue } from '../utils/helpers';
import {
  X,
  Calendar,
  FolderKanban,
  Trash2,
  CheckSquare,
  Square,
  ArrowRight,
  Tag,
} from 'lucide-react';
import './TaskDetail.css';

export default function TaskDetail() {
  const { state, dispatch } = useApp();
  const task = state.tasks.find((t) => t.id === state.selectedTaskId);
  const [notes, setNotes] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const titleRef = useRef(null);

  useEffect(() => {
    if (task) {
      setNotes(task.notes || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id]);

  if (!task) return null;

  const handleTitleChange = (e) => {
    dispatch({
      type: 'UPDATE_TASK',
      payload: { id: task.id, title: e.target.value },
    });
  };

  const handleNotesBlur = () => {
    dispatch({
      type: 'UPDATE_TASK',
      payload: { id: task.id, notes },
    });
  };

  const handleDateChange = (e) => {
    dispatch({
      type: 'UPDATE_TASK',
      payload: { id: task.id, dueDate: e.target.value ? new Date(e.target.value).toISOString() : null },
    });
  };

  const handleAddChecklistItem = (e) => {
    e.preventDefault();
    if (!newChecklistItem.trim()) return;
    dispatch({
      type: 'ADD_CHECKLIST_ITEM',
      payload: { taskId: task.id, title: newChecklistItem.trim() },
    });
    setNewChecklistItem('');
  };

  const handleToggleChecklistItem = (itemId) => {
    dispatch({
      type: 'TOGGLE_CHECKLIST_ITEM',
      payload: { taskId: task.id, itemId },
    });
  };

  const handleDeleteChecklistItem = (itemId) => {
    dispatch({
      type: 'DELETE_CHECKLIST_ITEM',
      payload: { taskId: task.id, itemId },
    });
  };

  const handleMoveToView = (status) => {
    dispatch({
      type: 'MOVE_TASK',
      payload: { id: task.id, status },
    });
  };

  const handleSetProject = (projectId) => {
    dispatch({
      type: 'UPDATE_TASK',
      payload: { id: task.id, projectId },
    });
  };

  const handleToggleTag = (tagId) => {
    const newTags = task.tags.includes(tagId)
      ? task.tags.filter((t) => t !== tagId)
      : [...task.tags, tagId];
    dispatch({
      type: 'UPDATE_TASK',
      payload: { id: task.id, tags: newTags },
    });
  };

  const handleDelete = () => {
    dispatch({ type: 'DELETE_TASK', payload: task.id });
    dispatch({ type: 'SET_SELECTED_TASK', payload: null });
  };

  const handleClose = () => {
    dispatch({ type: 'SET_SELECTED_TASK', payload: null });
  };

  const overdue = isOverdue(task.dueDate);

  const views = [
    { key: 'inbox', label: 'Inbox', icon: '📥' },
    { key: 'today', label: 'Today', icon: '☀️' },
    { key: 'anytime', label: 'Anytime', icon: '📋' },
    { key: 'someday', label: 'Someday', icon: '💭' },
  ];

  return (
    <div className="task-detail">
      <div className="task-detail-header">
        <h3>Task Details</h3>
        <button className="detail-close" onClick={handleClose}>
          <X size={18} />
        </button>
      </div>

      <div className="task-detail-body">
        <input
          ref={titleRef}
          className="detail-title-input"
          value={task.title}
          onChange={handleTitleChange}
          placeholder="Task title..."
        />

        <textarea
          className="detail-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={handleNotesBlur}
          placeholder="Add notes..."
          rows={3}
        />

        <div className="detail-section">
          <div className="detail-row">
            <span className="detail-label">
              <Calendar size={14} /> Due Date
            </span>
            <div className="detail-value">
              {task.dueDate ? (
                <span className={`detail-date ${overdue ? 'overdue' : ''}`}>
                  {formatDueDate(task.dueDate)}
                  <button
                    className="detail-clear"
                    onClick={() =>
                      dispatch({
                        type: 'UPDATE_TASK',
                        payload: { id: task.id, dueDate: null, dueTime: null },
                      })
                    }
                  >
                    <X size={12} />
                  </button>
                </span>
              ) : (
                <input
                  type="date"
                  className="detail-date-input"
                  onChange={handleDateChange}
                />
              )}
            </div>
          </div>

          <div className="detail-row">
            <span className="detail-label">
              <ArrowRight size={14} /> Move to
            </span>
            <div className="detail-value">
              <div className="detail-move-options">
                {views.map((v) => (
                  <button
                    key={v.key}
                    className={`move-option ${task.status === v.key ? 'active' : ''}`}
                    onClick={() => handleMoveToView(v.key)}
                  >
                    {v.icon} {v.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="detail-row">
            <span className="detail-label">
              <FolderKanban size={14} /> Project
            </span>
            <div className="detail-value">
              <select
                className="detail-select"
                value={task.projectId || ''}
                onChange={(e) => handleSetProject(e.target.value || null)}
              >
                <option value="">No project</option>
                {state.projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="detail-row">
            <span className="detail-label">
              <Tag size={14} /> Tags
            </span>
            <div className="detail-value">
              <div className="detail-tags">
                {state.tags.map((tag) => (
                  <button
                    key={tag.id}
                    className={`detail-tag ${
                      task.tags.includes(tag.id) ? 'active' : ''
                    }`}
                    style={{
                      backgroundColor: task.tags.includes(tag.id)
                        ? tag.color + '20'
                        : 'transparent',
                      borderColor: tag.color,
                      color: task.tags.includes(tag.id) ? tag.color : '#888',
                    }}
                    onClick={() => handleToggleTag(tag.id)}
                  >
                    {tag.name}
                  </button>
                ))}
                {state.tags.length === 0 && (
                  <span className="no-tags">No tags created yet</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <div className="detail-section-header">
            <CheckSquare size={14} />
            <span>Checklist</span>
            <span className="checklist-count">
              {task.checklist.filter((i) => i.completed).length}/{task.checklist.length}
            </span>
          </div>

          <div className="checklist-items">
            {task.checklist.map((item) => (
              <div key={item.id} className="checklist-item">
                <button
                  className={`checklist-checkbox ${item.completed ? 'checked' : ''}`}
                  onClick={() => handleToggleChecklistItem(item.id)}
                >
                  {item.completed ? (
                    <CheckSquare size={14} />
                  ) : (
                    <Square size={14} />
                  )}
                </button>
                <span className={`checklist-title ${item.completed ? 'completed' : ''}`}>
                  {item.title}
                </span>
                <button
                  className="checklist-delete"
                  onClick={() => handleDeleteChecklistItem(item.id)}
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>

          <form className="checklist-add" onSubmit={handleAddChecklistItem}>
            <input
              value={newChecklistItem}
              onChange={(e) => setNewChecklistItem(e.target.value)}
              placeholder="Add checklist item..."
              className="checklist-add-input"
            />
          </form>
        </div>

        <div className="detail-footer">
          <span className="detail-created">
            Created {new Date(task.createdAt).toLocaleDateString()}
          </span>
          <button className="detail-delete" onClick={handleDelete}>
            <Trash2 size={14} />
            Delete Task
          </button>
        </div>
      </div>
    </div>
  );
}
